import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { Category, Item, ItemService } from '../../../item-detail/item-detail.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { map } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDrawer } from '@angular/material/sidenav';
import { AppMessageService } from '../../../../app.message.service';

export interface IFilterData {
  filter: string;
  filterTags: Set<string>;
}

@Component({
  selector: 'app-filters',
  standalone: true,
  imports: [CommonModule, MatButtonModule, RouterModule, MatTooltipModule,
    MatDividerModule, ReactiveFormsModule, FormsModule],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './filters.component.html',
  styleUrl: './filters.component.scss'
})
export class FiltersComponent implements OnInit {

  mainFilter = 'all';
  priceSort: 'LOW' | 'LATEST' | 'HIGH' = 'LATEST';
  aviFilter = '';
  IN_STOCK = false;
  OUT_OF_STOCK = false;
  PRE_ORDER = false;

  cats: Category[] = [];
  cat: Category = { id: '' };

  dynamicFilters = new Set<string>();
  dynamicAvai = new Set<string>();

  filters: IFilterData[] = [];

  @Input() items: Item[] = [];
  itemsFilter: Item[] = [];
  @Output() itemEvent = new EventEmitter<Item[]>();
  @Output() category = new EventEmitter<Category>();

  constructor(
    private msgService: AppMessageService,
    private itemService: ItemService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.data.subscribe(g => {
      const w: Category[] = g['cats'] || [];
      const currentSlug = this.route.snapshot.paramMap.get('cat');
      this.cats = w.filter(item => item.slug === currentSlug);
      this.cat = this.cats[0] || { id: '', filters: [] };
      if (this.cat.id) {
        this.category.emit(this.cat);
      }
      this.filters = [];
      if (this.cat.filters) {
        this.cat.filters.forEach((value) => {
          this.filters.push({
            filter: value.label!,
            filterTags: new Set<string>()
          });
        });
      }

      this.resetFilters();

      this.items = g['items'] || [];
      this.itemsFilter = g['items'] || [];
      this.route.queryParams.subscribe(params => {
        if (params['variations'] !== undefined) {
          if (params['variations'].length > 0) {
            this.dynamicFilters = new Set<string>(params['variations'].split(','));
          }
        }
        if (params['availability'] !== undefined) {
          if (params['availability'].length > 0) {
            this.dynamicAvai = new Set<string>(params['availability'].split(','));
          }
        }

        if (params['sort'] !== undefined) {
          if (params['sort'] === 'HIGH' || params['sort'] === 'LOW' || params['sort'] === 'LATEST') {
            this.priceSort = params['sort'];
          }
        }
        if (params['stock'] !== undefined) {
          if (params['stock'].length > 0) {
            this.mainFilter = params['stock'];
          }
        }

        if (Object.keys(params).length === 0) {
          this.filterData(true);
          return;
        }
        const activeCat = w.find(c => c.slug === currentSlug);
        if (activeCat && activeCat.filters) {
          activeCat.filters.forEach((value) => {
            if (params[value.label!] !== undefined) {
              if (params[value.label!].length > 0) {
                params[value.label!].split(',').forEach((tag: string) => {
                  const filterObj = this.filters.find(v => v.filter === value.label!);
                  if (filterObj && filterObj.filterTags) {
                    filterObj.filterTags.add(tag);
                  }
                });
              }
            }
          });
        }
        this.filterData();
      });
    });
  }

  hideFilter() {
    this.msgService.pushNotification('HIDE_FILTER', '');
  }

  resetFilters() {
    this.mainFilter = 'all';
    this.priceSort = 'LATEST';
    this.aviFilter = '';
    this.IN_STOCK = false;
    this.OUT_OF_STOCK = false;
    this.PRE_ORDER = false;
    this.dynamicFilters = new Set<string>();
    this.dynamicAvai = new Set<string>();
    this.filters = [];
    if (this.cat && this.cat.filters) {
      this.cat.filters.forEach((value) => {
        this.filters.push({
          filter: value.label!,
          filterTags: new Set<string>()
        });
      });
    }
    this.filterData(true);
  }

  basicFilter(s: string) {
    this.mainFilter = s;
    this.filterData();
  }

  setPriceSort(sort: 'LOW' | 'LATEST' | 'HIGH') {
    this.priceSort = sort;
    this.filterData();
  }

  pushOrRemoveAvailability(s: string) {
    if (this.dynamicAvai.has(s)) {
      this.dynamicAvai.delete(s);
    } else {
      this.dynamicAvai.add(s);
    }

    this.filterData();
  }

  pushOrRemoveFilter(fil: string, s: string) {
    this.filters.find(q => q.filter === fil)?.filterTags;
    if (this.filters.find(q => q.filter === fil)?.filterTags.has(s)) {
      this.filters.find(q => q.filter === fil)?.filterTags.delete(s);
    } else {
      this.filters.find(q => q.filter === fil)?.filterTags.add(s);
    }

    this.filterData();
  }

  isFilterItemAdded(fil: string, s: string): boolean {
    return this.filters.find(q => q.filter === fil)!.filterTags!.has(s);
  }

  isAvaiItemAdded(s: string): boolean {
    return this.dynamicAvai.has(s);
  }

  filterData(defaultState = false) {
    let g = [...this.items];
    this.itemsFilter = g.filter(e => {
      if (this.mainFilter === 'all') {
        return true;
      }
      if (this.mainFilter === 'latest-additions') {
        return e.newArrival;
      }
      if (this.mainFilter === 'exclusive-deals') {
        return (e.discounted);
      }

      if (this.mainFilter === 'brand-new') {
        return e.brandNew;
      }
      if (this.mainFilter === 'used') {
        return e.used;
      }

      return false;
    })
      .filter(q => {
        if (this.dynamicAvai.size > 0) {
          return this.dynamicAvai.has(q.availability!);
        } else {
          return true;
        }
      });

    const filteredItems = this.itemsFilter.filter(item =>
      this.filters.every(filter => !filter.filterTags.size || Array.from(filter.filterTags).some(tag => item.tags?.includes(tag)))
    );

    this.itemsFilter = filteredItems;

    let params = new HttpParams();
    params = params.append('variations', Array.from(this.dynamicFilters).join(','));
    params = params.append('stock', this.mainFilter);
    params = params.append('sort', this.priceSort);
    params = params.append('availability', Array.from(this.dynamicAvai).join(','));
    this.filters.forEach(w => {
      params = params.append(w.filter, Array.from(w.filterTags).join(','));
    });
    if (!defaultState) {
      this.router.navigateByUrl('/' + this.cat.slug + '?' + params.toString());
    }

    const getPrice = (item: Item): number | null => {
      const np = Number(item.newPrice);
      if (!isNaN(np) && np > 0) {
        return np;
      }
      const pp = Number(item.previousPrice);
      if (!isNaN(pp) && pp > 0) {
        return pp;
      }
      return null;
    };

    const sortItems = (items: Item[], sortMode: 'LOW' | 'LATEST' | 'HIGH'): Item[] => {
      if (sortMode === 'LATEST') {
        return [...items].sort((a, b) => (b.id || '').localeCompare(a.id || ''));
      }

      return [...items].sort((a, b) => {
        const priceA = getPrice(a);
        const priceB = getPrice(b);

        if (priceA === null && priceB === null) {
          return (b.id || '').localeCompare(a.id || '');
        }
        if (priceA === null) return 1;
        if (priceB === null) return -1;

        if (priceA === priceB) {
          return (b.id || '').localeCompare(a.id || '');
        }

        return sortMode === 'LOW' ? priceA - priceB : priceB - priceA;
      });
    };

    const inStockItems = this.itemsFilter.filter(e => e.availability !== 'OUT_OF_STOCK');
    const outOfStockItems = this.itemsFilter.filter(e => e.availability === 'OUT_OF_STOCK');

    const sortedInStock = sortItems(inStockItems, this.priceSort);
    const sortedOutOfStock = sortItems(outOfStockItems, this.priceSort);

    this.itemEvent.emit([...sortedInStock, ...sortedOutOfStock]);
  }

}

// if (this.dynamicFilters.size > 0) {

//   this.dynamicFilters.forEach(f => {
//     [...this.itemsFilter].filter(r => r.tags!.includes(f)).forEach(w => {
//       d.add(w)
//     })

//   })
//   this.itemsFilter = Array.from(d)

// } else {
//   d = new Set<Item>(Array.from(this.itemsFilter))
//   this.itemsFilter = Array.from(d)

// }